import {useState, useEffect, useRef} from 'react'
import {documentToPlainTextString} from '@contentful/rich-text-plain-text-renderer'
import {documentToHtmlString} from '@contentful/rich-text-html-renderer'
import type {GenericContent, GenericCTAGroup} from '../../../../../../brand/lib/types/contentful'

import {Heading, Image, Prose} from '@primer/react-brand'
import {TriangleDownIcon} from '@primer/octicons-react'
import {CtaButtons} from '../../_shared/CtaButtons/CtaButtons'
import {Label} from '../../_shared/Label/Label'
import {Spacer} from '../../_shared/Spacer/Spacer'
import {animateFadeIn} from '../../_shared/animations/index'
import Styles from './RiverAccordion.module.css'

type Props = {
  content: GenericContent
  location: string
  index: number
  sectionIndex: number
}

export function RiverAccordion(props: Props) {
  const {content, index, location, sectionIndex} = props
  const {heading, label, ctas} = content.fields

  const title = heading
  const eyebrow = label?.fields.text
  const ctaGroup = ctas?.at(0) as GenericCTAGroup

  // Accordion
  const items = (content.fields.content as GenericContent[] | undefined) ?? []
  const [expandedIndex, setExpandedIndex] = useState<number>(0)
  const [previousIndex, setPreviousIndex] = useState<number | null>(null)

  const handleExpandItem = (itemIndex: number) => {
    setPreviousIndex(expandedIndex)
    setExpandedIndex(itemIndex)
  }

  const riverContentRef = useRef<HTMLDivElement>(null)
  const headingRef = useRef<HTMLDivElement>(null)
  const accordionRef = useRef<HTMLDivElement>(null)
  const ctaRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const elements: HTMLDivElement[] = []
    if (headingRef.current) elements.push(headingRef.current)
    if (accordionRef.current) elements.push(accordionRef.current)
    if (ctaRef.current) elements.push(ctaRef.current)

    animateFadeIn(elements, riverContentRef.current!)
  }, [])

  return (
    <div className={Styles.RiverAccordion} data-direction={index % 2 === 0 ? 'left' : 'right'}>
      <div className={Styles.Cell}>
        <div className={Styles.Content} ref={riverContentRef}>
          {eyebrow && (
            <>
              <Label text={eyebrow} />
              <Spacer size="64px" size1280="0" />
            </>
          )}

          {title && (
            <>
              <Heading data-heading="2" as={sectionIndex === 0 ? 'h2' : 'h3'} ref={headingRef}>
                {title}
              </Heading>
              <Spacer size="16px" size1280="0" />
            </>
          )}

          {/* Accordion */}
          <div ref={accordionRef}>
            {items
              .filter(item => {
                const {subheading, text} = item.fields
                const summary = subheading ? documentToPlainTextString(subheading) : ''
                const description = text ? documentToPlainTextString(text) : ''
                return summary && description
              })
              .map((item, itemIndex) => {
                // Accordion item
                const {subheading, text} = item.fields
                const summary = documentToPlainTextString(subheading!)
                if (!text) return null
                const description = documentToHtmlString(text)
                const imageAlt = item.fields.media?.[0]?.fields?.asset?.fields?.description || ''

                const isExpanded = expandedIndex === itemIndex

                return (
                  <div className={Styles.AccordionItem} key={item.sys?.id || itemIndex} data-expanded={isExpanded}>
                    <button
                      className={Styles.AccordionSummary}
                      onClick={() => handleExpandItem(itemIndex)}
                      aria-expanded={isExpanded}
                    >
                      {summary}
                      <TriangleDownIcon className={Styles.AccordionIcon} />
                    </button>

                    <div className={Styles.AccordionContainer}>
                      <div className={Styles.AccordionContent}>
                        <Prose className={Styles.Description} html={description} />

                        {/* A11y: Read image alt text */}
                        {imageAlt && <span className="sr-only">{imageAlt}</span>}
                      </div>
                    </div>
                  </div>
                )
              })}
          </div>

          {ctas && (
            <>
              <Spacer size="64px" size1280="0" />
              <div ref={ctaRef}>
                <CtaButtons contentfulContent={ctaGroup} location={location} />
              </div>
            </>
          )}
        </div>
      </div>

      <div className={Styles.Cell}>
        {items.map((item, itemIndex) => {
          const image = item.fields.media?.[0]?.fields
          if (!image?.asset.fields.file.details?.image) return null // Only allow images

          return (
            <Image
              className={Styles.Image}
              key={item.sys?.id || itemIndex}
              src={image.asset.fields.file.url}
              width={image.width || image.asset.fields.file.details?.image?.width || ''}
              height={image.height || image.asset.fields.file.details?.image?.height || ''}
              data-current={itemIndex === expandedIndex}
              data-last={itemIndex === previousIndex}
              alt=""
              loading="lazy"
            />
          )
        })}
      </div>
    </div>
  )
}

try{ RiverAccordion.displayName ||= 'RiverAccordion' } catch {}